import { useStore } from './index';
import { type History, pickHistory } from './partialize';
import type { RootState } from './slices';

type Runtime = {
  source: Pick<RootState['source'], 'fileName' | 'dimensions' | 'imageData' | 'revision'>;
  // `cleaned` is a runtime-only buffer; the three tool settings are excluded
  // from history (so adjusting them doesn't push an undo step) but must
  // survive the slice merge on undo/redo.
  background: Pick<
    RootState['background'],
    'cleaned' | 'wandTolerance' | 'brushRadius' | 'brushSoftness'
  >;
  grid: Pick<RootState['grid'], 'autoConfidence'>;
  // `blobs` and `detecting` are derived/transient and not in History — without
  // preservation, undo blanks the CCL overlay until a re-detect (page reload).
  pin: Pick<RootState['pin'], 'blobs' | 'detecting'>;
  animation: Pick<RootState['animation'], 'selectedRow' | 'isPlaying' | 'currentFrame'>;
  editor: Pick<
    RootState['editor'],
    | 'drawMode'
    | 'selectedEdge'
    | 'selectedBoxes'
    | 'selectedBlobs'
    | 'hoveredRegionId'
    | 'selectedRegionId'
    | 'viewport'
    | 'processing'
  >;
  // `step` is a navigation preference, not a data state — keep it ephemeral
  // so undo/redo doesn't hop the user back to a previous matte/sprite view.
  workflow: Pick<RootState['workflow'], 'committedCleaned' | 'step'>;
  // Template-matching results aren't in History; without preservation, undo
  // blanks the per-frame confidence badges and pivot overlay until reload.
  referencePoint: Pick<RootState['referencePoint'], 'results'>;
};

function pickRuntime(s: RootState): Runtime {
  return {
    source: {
      fileName: s.source.fileName,
      dimensions: s.source.dimensions,
      imageData: s.source.imageData,
      revision: s.source.revision,
    },
    background: {
      cleaned: s.background.cleaned,
      wandTolerance: s.background.wandTolerance,
      brushRadius: s.background.brushRadius,
      brushSoftness: s.background.brushSoftness,
    },
    grid: { autoConfidence: s.grid.autoConfidence },
    pin: { blobs: s.pin.blobs, detecting: s.pin.detecting },
    animation: {
      selectedRow: s.animation.selectedRow,
      isPlaying: s.animation.isPlaying,
      currentFrame: s.animation.currentFrame,
    },
    editor: {
      drawMode: s.editor.drawMode,
      selectedEdge: s.editor.selectedEdge,
      selectedBoxes: s.editor.selectedBoxes,
      selectedBlobs: s.editor.selectedBlobs,
      hoveredRegionId: s.editor.hoveredRegionId,
      selectedRegionId: s.editor.selectedRegionId,
      viewport: s.editor.viewport,
      processing: s.editor.processing,
    },
    workflow: { committedCleaned: s.workflow.committedCleaned, step: s.workflow.step },
    referencePoint: { results: s.referencePoint.results },
  };
}

// zundo's built-in undo/redo applies the snapshot via a separate `userSet`,
// then we'd need a second `setState` to graft runtime fields back. Between the
// two, React commits an intermediate render and direct store subscribers (e.g.
// livedetect) fire — both seeing wiped runtime fields. The visible symptom:
// pivot overlay and template-match badges flicker out and don't recover.
//
// Instead, replicate undo/redo manually so the snapshot apply and the runtime
// graft happen inside a single mutative draft — one notification, one render,
// no observable intermediate state.
function applyHistory(direction: 'undo' | 'redo'): void {
  const temporal = useStore.temporal.getState();
  const source = direction === 'undo' ? temporal.pastStates : temporal.futureStates;
  if (source.length === 0) return;

  const remaining = source.slice(0, -1);
  const target = source[source.length - 1] as Partial<History> | undefined;
  if (!target) return;

  const current = useStore.getState();
  const currentSnapshot = pickHistory(current);
  const runtime = pickRuntime(current);

  // Pause history tracking so our atomic apply is not itself recorded as a
  // new step (which would clobber the redo stack).
  temporal.pause();
  useStore.setState((s) => {
    for (const key of Object.keys(target) as (keyof History)[]) {
      const slice = target[key];
      if (slice && typeof slice === 'object') {
        Object.assign(s[key], slice);
      }
    }
    Object.assign(s.source, runtime.source);
    Object.assign(s.background, runtime.background);
    Object.assign(s.grid, runtime.grid);
    Object.assign(s.pin, runtime.pin);
    Object.assign(s.animation, runtime.animation);
    Object.assign(s.editor, runtime.editor);
    Object.assign(s.workflow, runtime.workflow);
    Object.assign(s.referencePoint, runtime.referencePoint);
  });
  temporal.resume();

  if (direction === 'undo') {
    useStore.temporal.setState({
      pastStates: remaining,
      futureStates: [...temporal.futureStates, currentSnapshot],
    });
  } else {
    useStore.temporal.setState({
      pastStates: [...temporal.pastStates, currentSnapshot],
      futureStates: remaining,
    });
  }
}

export function undo(): void {
  applyHistory('undo');
}

export function redo(): void {
  applyHistory('redo');
}
